import React, { useContext, useEffect, useMemo, useState } from "react";
import DateFilterDropdown from "../../Shared/DateFilterDropdown/DateFilterDropdown";
import { InlineFilterSelect } from "../shared/FilterSelect";
import SpotOverviewTab from "../tabs/SpotOverviewTab";
import classes from "../MasterDashboard.module.css";
import { useSpotOverview } from "../hooks/useSpotOverview";
import useIsSuperAdmin from "../../../hooks/useIsSuperAdmin";
import { useMixPanelForTracking } from "../../../utils/TrackMixPanel";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../MyCompanyProfileContext";
import {
  ROLE_OPTIONS,
  PERIOD_PRESET_FILTERS,
  toIsoDate,
  formatRelativeTime,
  transformSpotMetrics,
  transformSpotFunnel,
  transformTopTransporters,
  transformLanesByPlant,
} from "../utils/transformers";
import { useSpotDashboardFilters } from "../hooks/Usespotdashboardfilters";

const SpotOverviewPanel = ({ isActive = true }) => {
  const isSuperAdmin = useIsSuperAdmin();
  const roleOptions = isSuperAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((opt) => opt.value !== "company_view");
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: "None",
      Definition: "user has visited spot overview tab",
    });
  }, []);

  const [role, setRole] = useState(ROLE_OPTIONS[0].value);
  useEffect(() => {
    if (!isSuperAdmin && role === "company_view") {
      setRole("user_view");
    }
  }, [isSuperAdmin, role]);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return { startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) };
  });
  // NOTE: plant/destination now hold the raw location_name string
  // (matching lane_details.origin.location.location /
  // lane_details.destination.location_name on the backend), not a
  // location_code. "All" is still the sentinel for "no filter".
  const [plant, setPlant] = useState("All");
  const [destination, setDestination] = useState("All");
  const [transporterId, setTransporterId] = useState("All");
  const [dateFilterKey, setDateFilterKey] = useState(0);

  const { startDate, endDate } = dateRange;

  const handleDateChange = ({ fromDate, toDate, selectedFilter }) => {
    setDateRange({ startDate: toIsoDate(new Date(fromDate)), endDate: toIsoDate(new Date(toDate)) });
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: selectedFilter || `${fromDate} - ${toDate}`,
      Definition: "user has changed period filter",
    });
  };

  const handleRoleChange = (value) => {
    setRole(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: value,
      Definition: "user has changed role filter",
    });
  };

  const handlePlantChange = (value) => {
    setPlant(value);
    setDestination("All");
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: value,
      Definition: "user has changed plant filter",
    });
  };

  const handleDestinationChange = (value) => {
    setDestination(value);
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: value,
      Definition: "user has changed destination filter",
    });
  };

  const handleTransporterChange = (value) => {
    setTransporterId(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: value,
      Definition: "user has changed transporter filter",
    });
  };

  const handleResetFilters = () => {
    setRole(ROLE_OPTIONS[0].value);
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setDateRange({ startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) });
    setPlant("All");
    setDestination("All");
    setTransporterId("All");
    setDateFilterKey((k) => k + 1);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "spot overview",
      Data: "None",
      Definition: "user has reset filters",
    });
  };

  const transporterIdParam = transporterId === "All" ? undefined : transporterId;
  const destinationParam = destination === "All" ? undefined : destination;
  const plantParam = plant === "All" ? undefined : plant;

  // Spot's own filters endpoint — NOT useTmsDashboardFilters (that one is
  // keyed by location_code and matches the Indent Summary backend, which
  // uses a completely different field to filter by plant/destination).
  const { data: filtersData } = useSpotDashboardFilters({
    startDate,
    endDate,
    role,
    plant: plantParam,
    destination: destinationParam,
    transporterId: transporterIdParam,
  });

  const plantOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.plant || [])
        .filter((p) => p.location_name)
        .map((p) => ({ value: p.location_name, label: p.location_name })),
    ],
    [filtersData]
  );

  const destinationOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.destination || [])
        .filter((d) => d.location_name)
        .map((d) => ({ value: d.location_name, label: d.location_name })),
    ],
    [filtersData]
  );

  const transporterOptions = useMemo(() => {
    const transporters = filtersData?.transporters || [];
    return [
      { value: "All", label: "All" },
      ...transporters.map((t) => ({ value: t.transporter_id, label: t.transporter_name })),
    ];
  }, [filtersData]);

  const {
    metrics: metricsData,
    funnel: funnelData,
    topTransporters: topTransportersData,
    lanes: lanesData,
    isLoading,
    dataUpdatedAt,
  } = useSpotOverview(
    { startDate, endDate, plant: plantParam, role, transporterId: transporterIdParam, destination: destinationParam },
    isActive
  );

  const metrics = useMemo(() => transformSpotMetrics(metricsData), [metricsData]);
  const funnelStages = useMemo(() => transformSpotFunnel(funnelData), [funnelData]);
  const topTransporters = useMemo(() => transformTopTransporters(topTransportersData), [topTransportersData]);
  const byPlant = useMemo(() => transformLanesByPlant(lanesData), [lanesData]);

  const lastUpdated = formatRelativeTime(dataUpdatedAt, now);

  return (
    <>
      <div className={classes.controlsBar}>
        <InlineFilterSelect label="Role" value={role} onChange={handleRoleChange} options={roleOptions} />
        <div className={classes.inlineFilterGroup}>
          <span className={classes.inlineFilterLabel}>Period</span>
          <DateFilterDropdown
            key={dateFilterKey}
            onDateChange={handleDateChange}
            defaultFilter="1 Week"
            presetFilters={PERIOD_PRESET_FILTERS}
          />
        </div>
        <InlineFilterSelect label="Plant" value={plant} onChange={handlePlantChange} options={plantOptions} />
        <InlineFilterSelect
          label="Destination"
          value={destination}
          onChange={handleDestinationChange}
          options={destinationOptions}
        />
        <InlineFilterSelect
          label="Transporter"
          value={transporterId}
          onChange={handleTransporterChange}
          options={transporterOptions}
        />
        <button
          type="button"
          className={classes.resetFiltersButton}
          onClick={handleResetFilters}
          title="Reset all filters"
        >
          ↺ Reset
        </button>
        <div className={classes.liveStatus}>
          <span className={classes.liveDot} />
          Live · updated {lastUpdated}
        </div>
      </div>

      <div className={classes.pageBody}>
        {isLoading ? (
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner} />
            <div className={classes.loadingText}>Loading spot overview…</div>
          </div>
        ) : metrics ? (
          <SpotOverviewTab
            metrics={metrics}
            funnelStages={funnelStages}
            topTransporters={topTransporters}
            byPlant={byPlant}
          />
        ) : null}
      </div>
    </>
  );
};

export default SpotOverviewPanel;